'use client';

import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface UserBehavior {
  dashboardsViewed: number;
  alertsOpened: number;
  favoriteModels: string[];
  aiInteractions: number;
  sessionTime: number;
  favoriteTeams: string[];
  lastActive: string;
}

interface Recommendation {
  id: string;
  type: 'dashboard' | 'alert' | 'model' | 'insight';
  title: string;
  reason: string;
  confidence: number;
  icon: string;
  color: string;
}

const MOCK_BEHAVIOR: UserBehavior = {
  dashboardsViewed: 847,
  alertsOpened: 234,
  favoriteModels: ['xG Neural', 'Hybrid Ensemble'],
  aiInteractions: 156,
  sessionTime: 4820,
  favoriteTeams: ['BOS', 'COL', 'TBL'],
  lastActive: '2026-05-16 22:41',
};

const RECOMMENDATIONS: Recommendation[] = [
  { id: 'r1', type: 'dashboard', title: 'Viz Engine — Shot Maps', reason: 'Basé sur votre intérêt pour les données xG', confidence: 94, icon: 'PresentationChartBarIcon', color: '#3b82f6' },
  { id: 'r2', type: 'alert', title: 'Alerte Gardien — BOS vs TOR', reason: 'Votre équipe favorite joue ce soir', confidence: 91, icon: 'BellAlertIcon', color: '#ef4444' },
  { id: 'r3', type: 'model', title: 'Hybrid Ensemble — Recalibration', reason: 'Votre modèle préféré vient d\'être mis à jour', confidence: 88, icon: 'CpuChipIcon', color: '#10b981' },
  { id: 'r4', type: 'insight', title: 'Anomalie Marché — COL vs VGK', reason: 'Divergence détectée sur votre équipe suivie', confidence: 85, icon: 'LightBulbIcon', color: '#f59e0b' },
  { id: 'r5', type: 'dashboard', title: 'Player Intelligence — Fatigue', reason: 'Pattern d\'utilisation: vous consultez souvent après B2B', confidence: 82, icon: 'UserGroupIcon', color: '#a855f7' },
];

const USAGE_TIMELINE = Array.from({ length: 7 }, (_, i) => ({
  day: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][i],
  sessions: Math.floor(Math.random() * 8 + 2),
  aiQueries: Math.floor(Math.random() * 15 + 3),
  alertsChecked: Math.floor(Math.random() * 12 + 1),
}));

const DASHBOARD_USAGE = [
  { name: 'Analytics Copilot', visits: 89, color: '#3b82f6' },
  { name: 'Market Intelligence', visits: 67, color: '#ef4444' },
  { name: 'Player Intelligence', visits: 54, color: '#10b981' },
  { name: 'SimulationLab', visits: 48, color: '#a855f7' },
  { name: 'AI Sentinel', visits: 41, color: '#f59e0b' },
  { name: 'Viz Engine', visits: 35, color: '#06b6d4' },
];

const AI_FEED = [
  { time: '22:41', type: 'insight', msg: 'Basé sur votre historique: BOS a 73% de chances ce soir selon vos modèles favoris' },
  { time: '21:15', type: 'alert', msg: 'Anomalie détectée sur COL — votre équipe suivie montre un signal fort' },
  { time: '20:30', type: 'model', msg: 'xG Neural (votre modèle préféré) vient d\'être recalibré +2.1% précision' },
  { time: '19:45', type: 'recommendation', msg: 'Nouveau: Simulation disponible pour TBL vs FLA — match à haute volatilité' },
];

export default function BehaviorAnalyticsClient() {
  const [activeSection, setActiveSection] = useState<'overview' | 'feed' | 'adaptive'>('overview');
  const [sessionTime, setSessionTime] = useState(MOCK_BEHAVIOR.sessionTime);

  useEffect(() => {
    const interval = setInterval(() => setSessionTime(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="UserCircleIcon" size={20} className="text-cyan-400" />
            <h1 className="text-xl font-bold text-foreground">Behavior Analytics</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-cyan-400/10 text-cyan-400 border border-cyan-400/20 rounded">PHASE QUANT · ÉTAPE 4</span>
          </div>
          <p className="text-sm text-muted-foreground">Intelligence comportementale pour améliorer expérience et projections</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">SESSION ACTIVE</div>
            <div className="text-sm font-bold text-cyan-400 font-mono">{formatTime(sessionTime)}</div>
          </div>
        </div>
      </div>

      {/* Behavior KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Dashboards Vus', value: MOCK_BEHAVIOR.dashboardsViewed.toLocaleString(), icon: 'ChartBarIcon', color: 'text-blue-400' },
          { label: 'Alertes Ouvertes', value: MOCK_BEHAVIOR.alertsOpened.toString(), icon: 'BellAlertIcon', color: 'text-red-400' },
          { label: 'Interactions IA', value: MOCK_BEHAVIOR.aiInteractions.toString(), icon: 'SparklesIcon', color: 'text-purple-400' },
          { label: 'Équipes Suivies', value: MOCK_BEHAVIOR.favoriteTeams.join(', '), icon: 'StarIcon', color: 'text-yellow-400' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xs text-muted-foreground font-mono uppercase">{kpi.label}</span>
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
            </div>
            <div className={`text-xl font-bold font-mono ${kpi.color}`}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['overview', 'feed', 'adaptive'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveSection(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeSection === tab ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab === 'overview' ? 'Comportement' : tab === 'feed' ? 'AI Feed Intelligent' : 'Dashboard Adaptatif'}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Utilisation par Dashboard</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={DASHBOARD_USAGE} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Bar dataKey="visits" radius={[0, 4, 4, 0]}>
                  {DASHBOARD_USAGE.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Activité Hebdomadaire</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={USAGE_TIMELINE}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 10 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Line type="monotone" dataKey="sessions" stroke="#06b6d4" strokeWidth={2} dot={false} name="Sessions" />
                <Line type="monotone" dataKey="aiQueries" stroke="#a855f7" strokeWidth={1.5} dot={false} name="Requêtes IA" />
                <Line type="monotone" dataKey="alertsChecked" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Alertes" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeSection === 'feed' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">AI Feed Personnalisé</h3>
            <div className="space-y-3">
              {AI_FEED.map((item, i) => (
                <div key={i} className="flex gap-3 p-3 bg-background rounded-lg border border-border">
                  <div className="text-2xs text-muted-foreground font-mono w-10 flex-shrink-0 pt-0.5">{item.time}</div>
                  <div>
                    <span className={`text-2xs px-1.5 py-0.5 rounded font-mono mr-2 ${item.type === 'alert' ? 'bg-red-400/10 text-red-400' : item.type === 'model' ? 'bg-green-400/10 text-green-400' : item.type === 'insight' ? 'bg-blue-400/10 text-blue-400' : 'bg-purple-400/10 text-purple-400'}`}>
                      {item.type.toUpperCase()}
                    </span>
                    <span className="text-xs text-foreground">{item.msg}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Recommandations Personnalisées</h3>
            <div className="space-y-2">
              {RECOMMENDATIONS.map(rec => (
                <div key={rec.id} className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border hover:border-cyan-400/30 transition-colors cursor-pointer">
                  <Icon name={rec.icon as Parameters<typeof Icon>[0]['name']} size={16} style={{ color: rec.color }} className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-foreground">{rec.title}</div>
                    <div className="text-2xs text-muted-foreground mt-0.5">{rec.reason}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-bold font-mono text-green-400">{rec.confidence}%</div>
                    <div className="text-2xs text-muted-foreground">match</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'adaptive' && (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Dashboard Adaptatif — Modules Suggérés</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { title: 'Goalie Watch', desc: 'Confirmations gardiens pour BOS, COL, TBL', priority: 'HIGH', color: 'border-red-400/30 bg-red-400/5' },
                { title: 'xG Neural Live', desc: 'Votre modèle préféré en mode temps réel', priority: 'HIGH', color: 'border-green-400/30 bg-green-400/5' },
                { title: 'Market Anomalies', desc: 'Divergences sur vos équipes suivies', priority: 'MED', color: 'border-yellow-400/30 bg-yellow-400/5' },
                { title: 'Fatigue Tracker', desc: 'B2B schedule pour équipes favorites', priority: 'MED', color: 'border-blue-400/30 bg-blue-400/5' },
              ].map(module => (
                <div key={module.title} className={`p-3 rounded-lg border ${module.color}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{module.title}</span>
                    <span className={`text-2xs px-1.5 py-0.5 rounded font-mono ${module.priority === 'HIGH' ? 'bg-red-400/10 text-red-400' : 'bg-yellow-400/10 text-yellow-400'}`}>{module.priority}</span>
                  </div>
                  <p className="text-2xs text-muted-foreground">{module.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Profil Comportemental</h3>
            <div className="space-y-3">
              <div className="p-3 bg-background rounded border border-border">
                <div className="text-2xs text-muted-foreground mb-1">Modèles Favoris</div>
                {MOCK_BEHAVIOR.favoriteModels.map(m => (
                  <span key={m} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-primary/10 text-primary text-2xs rounded border border-primary/20">{m}</span>
                ))}
              </div>
              <div className="p-3 bg-background rounded border border-border">
                <div className="text-2xs text-muted-foreground mb-1">Équipes Suivies</div>
                {MOCK_BEHAVIOR.favoriteTeams.map(t => (
                  <span key={t} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-yellow-400/10 text-yellow-400 text-2xs rounded border border-yellow-400/20 font-mono">{t}</span>
                ))}
              </div>
              <div className="p-3 bg-background rounded border border-border">
                <div className="text-2xs text-muted-foreground mb-1">Dernière Activité</div>
                <div className="text-xs text-foreground font-mono">{MOCK_BEHAVIOR.lastActive}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
